import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { UserPlus } from "lucide-react";

/**
 * iter64 Phase B + C · Approval Panel on OrderDetailPage.
 *
 * Extracted from the OrderDetailPage in Phase C DX cleanup — the page had
 * grown past the ~700-line split threshold.
 *
 * Covers spec sections:
 *   §15 — record an approval method
 *   §16 — grant internal approval on a Golf Shop Order
 *   §18 — golf-shop completion actions (Stocked / Distributed / etc.)
 *   §6  — invite a guest member to register (Guest Member Orders only)
 */
const APPROVAL_METHODS = [
  "No Approval Required", "Verbal Approval", "Phone Approval", "Email Approval",
  "Text Approval", "Written Approval", "Staff Authorized", "Other",
];
const GS_COMPLETION_ACTIONS = ["Stocked", "Distributed", "Event Ready", "Delivered to Department", "Completed"];

export default function ApprovalPanel({ order, onUpdated }) {
  const isGS = order.order_type === "Golf Shop Order";
  const isGuest = order.order_type === "Guest Member Order";
  const [method, setMethod] = useState(APPROVAL_METHODS[1]);
  const [approvedBy, setApprovedBy] = useState("");
  const [note, setNote] = useState("");
  const [gsAction, setGsAction] = useState(GS_COMPLETION_ACTIONS[0]);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const errorText = (err) =>
    (formatApiErrorDetail(err?.response?.data?.detail) || err?.message);

  const record = async () => {
    setBusy(true); setFeedback(null);
    try {
      const { data } = await api.post(`/admin/orders/${order.order_id}/approval`, {
        method,
        approved_by_name: approvedBy.trim() || undefined,
        note: note.trim() || undefined,
      });
      onUpdated?.(data);
      setFeedback({ type: "success", text: "Approval recorded." });
      setNote("");
    } catch (err) {
      setFeedback({ type: "error", text: errorText(err) || "Could not record approval." });
    } finally { setBusy(false); }
  };

  const grantInternal = async () => {
    setBusy(true); setFeedback(null);
    try {
      const { data } = await api.post(`/admin/orders/${order.order_id}/internal-approval`, {
        approver_role: approvedBy.trim() || "Manager",
        note: note.trim() || undefined,
      });
      onUpdated?.(data);
      setFeedback({ type: "success", text: "Internal approval granted." });
    } catch (err) {
      setFeedback({ type: "error", text: errorText(err) || "Could not grant internal approval." });
    } finally { setBusy(false); }
  };

  const recordCompletion = async () => {
    setBusy(true); setFeedback(null);
    try {
      const { data } = await api.post(`/admin/orders/${order.order_id}/gs-completion`, {
        action: gsAction,
        note: note.trim() || undefined,
      });
      onUpdated?.(data);
      setFeedback({ type: "success", text: `Marked ${gsAction}.` });
    } catch (err) {
      setFeedback({ type: "error", text: errorText(err) || "Could not record completion." });
    } finally { setBusy(false); }
  };

  const inviteToRegister = async () => {
    setBusy(true); setFeedback(null);
    try {
      const { data } = await api.post(`/admin/orders/${order.order_id}/invite-to-register`);
      setFeedback({
        type: "success",
        text: data.email_dispatched
          ? `Invite emailed to ${data.invite_sent_to || "the member"}.`
          : "Invite recorded. Share the /register link with the member manually.",
      });
    } catch (err) {
      setFeedback({ type: "error", text: errorText(err) || "Could not send invite." });
    } finally { setBusy(false); }
  };

  const showInternal = isGS && order.status === "awaiting_internal_approval";
  const showCompletion = isGS && ["receiving", "ready", "requested", "researching", "placed_with_vendor"].includes(order.status);
  const showInvite = isGuest && !order.member_id;

  return (
    <div className="p-6 bg-white border border-[#E5E3D8]" data-testid="approval-panel">
      <div className="overline">Approvals & Completion</div>
      <h3 className="font-serif text-xl text-[#1A422D] mt-1">Record an approval</h3>

      {order.approval && (
        <div className="mt-3 p-3 bg-[#F9F8F4] border border-[#F0EDE0] text-xs text-[#4A4A4A]" data-testid="approval-current">
          Latest: <strong>{order.approval.method}</strong>
          {order.approval.approved_by_name && <> · by {order.approval.approved_by_name}</>}
          <span className="text-[#7A7A7A]"> · recorded {order.approval.recorded_at?.slice(0, 10)}</span>
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="label-elegant block mb-1">Approval Method</label>
          <select value={method} onChange={(e) => setMethod(e.target.value)}
                  data-testid="approval-method" className="input-field">
            {APPROVAL_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="label-elegant block mb-1">Approved By (name/role)</label>
          <input value={approvedBy} onChange={(e) => setApprovedBy(e.target.value)}
                 data-testid="approval-by" className="input-field" placeholder="Head Pro / Member" />
        </div>
        <div className="md:col-span-2">
          <label className="label-elegant block mb-1">Note (optional)</label>
          <input value={note} onChange={(e) => setNote(e.target.value)}
                 data-testid="approval-note" className="input-field" placeholder="Confirmed by phone on Aug 4" />
        </div>
      </div>

      {feedback && (
        <div className={`mt-3 text-xs ${feedback.type === "error" ? "text-[#a33]" : "text-[#1A422D]"}`} data-testid="approval-feedback">
          {feedback.text}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="button" onClick={record} disabled={busy}
                data-testid="approval-record"
                className="btn-primary text-xs">
          {busy ? "Recording…" : "Record Approval"}
        </button>

        {showInternal && (
          <button type="button" onClick={grantInternal} disabled={busy}
                  data-testid="approval-internal-grant"
                  className="btn-primary text-xs bg-[#B59A5A] hover:bg-[#9C8046]">
            Grant Internal Approval → Requested
          </button>
        )}

        {showInvite && (
          <button type="button" onClick={inviteToRegister} disabled={busy}
                  data-testid="invite-to-register"
                  className="btn-secondary text-xs inline-flex items-center gap-2">
            <UserPlus size={13} /> Invite Member to Register
          </button>
        )}
      </div>

      {showCompletion && (
        <div className="mt-6 border-t border-[#F0EDE0] pt-4" data-testid="gs-completion-block">
          <div className="text-[10px] uppercase tracking-widest text-[#7A7A7A] font-semibold">Golf Shop Completion</div>
          <div className="mt-2 flex items-center gap-2">
            <select value={gsAction} onChange={(e) => setGsAction(e.target.value)}
                    data-testid="gs-completion-action" className="input-field max-w-xs">
              {GS_COMPLETION_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <button type="button" onClick={recordCompletion} disabled={busy}
                    data-testid="gs-completion-submit"
                    className="btn-secondary text-xs">
              Mark
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
